import {
  ArrowPathIcon,
  ChatBubbleLeftEllipsisIcon,
  SparklesIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline'
import { cn } from '@duck/ui'
import { useTar } from '@/modules'
import { GuideLastUpdated, IntentRelatedLinks } from './IntentRelatedLinks'

const FEEDBACK_MODEL_CARDS = [
  {
    titleKey: 'feedbackPage.modelCards.aiPatterns.title',
    descriptionKey: 'feedbackPage.modelCards.aiPatterns.description',
    icon: SparklesIcon,
  },
  {
    titleKey: 'feedbackPage.modelCards.humanJudgment.title',
    descriptionKey: 'feedbackPage.modelCards.humanJudgment.description',
    icon: UserCircleIcon,
  },
  {
    titleKey: 'feedbackPage.modelCards.growthLoop.title',
    descriptionKey: 'feedbackPage.modelCards.growthLoop.description',
    icon: ArrowPathIcon,
  },
] as const

const FEEDBACK_REVIEW_COLUMNS = [
  {
    titleKey: 'feedbackPage.reviewColumns.strengthsTitle',
    descriptionKey: 'feedbackPage.reviewColumns.strengthsDescription',
  },
  {
    titleKey: 'feedbackPage.reviewColumns.gapsTitle',
    descriptionKey: 'feedbackPage.reviewColumns.gapsDescription',
  },
  {
    titleKey: 'feedbackPage.reviewColumns.tradeoffsTitle',
    descriptionKey: 'feedbackPage.reviewColumns.tradeoffsDescription',
  },
  {
    titleKey: 'feedbackPage.reviewColumns.nextStepTitle',
    descriptionKey: 'feedbackPage.reviewColumns.nextStepDescription',
  },
] as const

const FEEDBACK_USAGE_KEYS = [
  'feedbackPage.usageItems.readForPatterns',
  'feedbackPage.usageItems.reworkTheDecision',
  'feedbackPage.usageItems.buildTransferableTaste',
] as const

export const XpLabFeedbackPage = () => {
  const { t } = useTar()

  return (
    <div className="flex flex-1 w-full justify-center">
      <div className="flex-1 min-w-0 max-w-6xl space-y-10 md:space-y-12 mt-4 md:mt-8 mb-16">
        <section
          className="grid grid-cols-1 md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] items-start gap-10 px-6 py-8 md:px-10 md:py-12"
          aria-labelledby="feedback-heading"
        >
          <div className="flex flex-col items-start gap-6 max-w-xl">
            <h1
              id="feedback-heading"
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-foreground"
            >
              {t('feedbackPage.title')}
              <span className="text-accent">.</span>
            </h1>
            <GuideLastUpdated isoDate="2026-05-09" />
            <p className="text-base md:text-lg text-foreground/90 leading-relaxed">
              {t('feedbackPage.introP1')}
            </p>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              {t('feedbackPage.introP2')}
            </p>
          </div>

          <aside className="rounded-xl border border-border bg-card/60 p-6 md:mt-8">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-accent">
              {t('feedbackPage.sidebarEyebrow')}
            </p>
            <p className="text-sm leading-7 text-foreground/90">
              {t('feedbackPage.sidebarBody')}
            </p>
          </aside>
        </section>

        <section className="space-y-8" aria-labelledby="model-heading">
          <div className="space-y-3">
            <h2
              id="model-heading"
              className="text-3xl font-bold text-foreground tracking-tight"
            >
              {t('feedbackPage.modelTitle')}
            </h2>
            <p className="text-muted-foreground text-lg max-w-3xl leading-relaxed">
              {t('feedbackPage.modelDescription')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3">
            {FEEDBACK_MODEL_CARDS.map((card) => {
              const Icon = card.icon
              return (
                <div
                  key={card.titleKey}
                  className="px-6 py-5 md:py-2 md:border-s md:border-border/50 md:first:border-s-0 space-y-3"
                >
                  <h3 className="flex items-baseline gap-2 text-lg font-semibold text-foreground">
                    <Icon aria-hidden className="h-4 w-4 self-center text-accent/70" />
                    {t(card.titleKey)}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    {t(card.descriptionKey)}
                  </p>
                </div>
              )
            })}
          </div>
        </section>

        <div aria-hidden className="h-px bg-border/50" />

        <section className="space-y-7" aria-labelledby="review-heading">
          <div className="space-y-3">
            <h2
              id="review-heading"
              className="text-3xl font-bold text-foreground tracking-tight"
            >
              {t('feedbackPage.reviewTitle')}
            </h2>
            <p className="text-muted-foreground text-lg max-w-3xl leading-relaxed">
              {t('feedbackPage.reviewDescription')}
            </p>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-2">
            {FEEDBACK_REVIEW_COLUMNS.map((column, index) => {
              const isLeftCol = index % 2 === 0
              return (
                <li
                  key={column.titleKey}
                  className={cn(
                    'px-1 py-6 md:px-7 md:py-7 space-y-2',
                    index < FEEDBACK_REVIEW_COLUMNS.length - 1 && 'border-b border-border/60',
                    index === FEEDBACK_REVIEW_COLUMNS.length - 2 && 'md:border-b-0',
                    isLeftCol && 'md:border-e md:border-border/60'
                  )}
                >
                  <h3 className="text-base md:text-lg font-semibold text-foreground leading-snug">
                    {t(column.titleKey)}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t(column.descriptionKey)}
                  </p>
                </li>
              )
            })}
          </ul>
        </section>

        <section
          className="relative isolate border-y border-border py-14 md:py-20"
          aria-labelledby="usage-heading"
        >
          <ChatBubbleLeftEllipsisIcon
            aria-hidden
            className="pointer-events-none absolute -end-12 -top-10 h-72 w-72 text-accent/[0.13] -rotate-6 select-none"
          />
          <div className="relative space-y-8">
            <div className="space-y-3 max-w-2xl">
              <h2
                id="usage-heading"
                className="text-3xl md:text-4xl font-bold tracking-tight text-foreground"
              >
                {t('feedbackPage.usageTitle')}
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                {t('feedbackPage.usageDescription')}
              </p>
            </div>

            <ol className="grid grid-cols-1 md:grid-cols-3">
              {FEEDBACK_USAGE_KEYS.map((key, index) => (
                <li
                  key={key}
                  className="px-6 py-5 md:py-3 md:border-s md:border-border/50 md:first:border-s-0 space-y-3"
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent/10 ring-1 ring-accent/20 text-sm font-semibold text-accent tabular-nums">
                      {index + 1}
                    </span>
                    <div aria-hidden className="h-px flex-1 bg-border/40" />
                  </div>
                  <p className="text-base leading-7 text-muted-foreground">{t(key)}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <IntentRelatedLinks
          source="xp_lab_feedback"
          links={[
            { to: '/faq#how-does-feedback-work-in-practice', label: t('feedbackPage.faqCardTitle') },
            { to: '/using-ai-without-outsourcing-judgment', label: t('intentResources.aiJudgmentTitle') },
            { to: '/take-home-assignments', label: t('intentResources.takeHomeAssignmentsTitle') },
          ]}
        />
      </div>
    </div>
  )
}
